import { getTextDirection, isRequiredLocaleCode, selectLocale, type RequiredLocaleCode } from "../../../packages/ui/src/localization.js";

export type AppearancePreference = "light" | "dark" | "system";
export type TextSizePreference = "standard" | "large";

export interface MobilePreferences {
  readonly locale: RequiredLocaleCode;
  readonly localeSource: "device" | "manual";
  readonly appearance: AppearancePreference;
  readonly textSize: TextSizePreference;
  readonly biometricLock: boolean;
  readonly notifications: Readonly<{
    event24Hours: boolean;
    event1Hour: boolean;
    creatorLive: boolean;
    tripReminders: boolean;
  }>;
}

export interface PreferencesStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const key = "portability.mobile.preferences.v1";

export function defaultMobilePreferences(deviceLocales: readonly string[]): MobilePreferences {
  const locale = selectLocale({ deviceLocales }).code as RequiredLocaleCode;
  return Object.freeze({
    locale,
    localeSource: "device" as const,
    appearance: "system" as const,
    textSize: "standard" as const,
    biometricLock: false,
    notifications: Object.freeze({ event24Hours: true, event1Hour: true, creatorLive: true, tripReminders: true })
  });
}

function validAppearance(value: unknown): value is AppearancePreference {
  return value === "light" || value === "dark" || value === "system";
}

function validTextSize(value: unknown): value is TextSizePreference {
  return value === "standard" || value === "large";
}

export function isMobilePreferences(value: unknown): value is MobilePreferences {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<MobilePreferences>;
  const notifications = candidate.notifications;
  return typeof candidate.locale === "string" && isRequiredLocaleCode(candidate.locale) &&
    (candidate.localeSource === "device" || candidate.localeSource === "manual") &&
    validAppearance(candidate.appearance) && validTextSize(candidate.textSize) &&
    typeof candidate.biometricLock === "boolean" &&
    typeof notifications === "object" && notifications !== null &&
    typeof notifications.event24Hours === "boolean" && typeof notifications.event1Hour === "boolean" &&
    typeof notifications.creatorLive === "boolean" && typeof notifications.tripReminders === "boolean";
}

export function loadMobilePreferences(storage: PreferencesStorage, deviceLocales: readonly string[]): MobilePreferences {
  const defaults = defaultMobilePreferences(deviceLocales);
  const raw = storage.getItem(key);
  if (raw === null) return defaults;
  try {
    const parsed = JSON.parse(raw) as Partial<MobilePreferences>;
    const locale = String(parsed.locale);
    if (!isRequiredLocaleCode(locale) || !validAppearance(parsed.appearance)) return defaults;
    const notifications = parsed.notifications ?? defaults.notifications;
    return Object.freeze({
      locale,
      localeSource: parsed.localeSource === "manual" ? "manual" : "device",
      appearance: parsed.appearance,
      textSize: validTextSize(parsed.textSize) ? parsed.textSize : defaults.textSize,
      biometricLock: parsed.biometricLock === true,
      notifications: Object.freeze({
        event24Hours: notifications.event24Hours !== false,
        event1Hour: notifications.event1Hour !== false,
        creatorLive: notifications.creatorLive !== false,
        tripReminders: notifications.tripReminders !== false
      })
    });
  } catch {
    return defaults;
  }
}

export function saveMobilePreferences(storage: PreferencesStorage, preferences: MobilePreferences): void {
  storage.setItem(key, JSON.stringify(preferences));
}

export function applyMobilePreferences(document: Pick<Document, "documentElement">, preferences: MobilePreferences): void {
  document.documentElement.lang = preferences.locale;
  document.documentElement.dir = getTextDirection(preferences.locale);
  if (preferences.appearance === "system") delete document.documentElement.dataset.appearance;
  else document.documentElement.dataset.appearance = preferences.appearance;
  document.documentElement.style.colorScheme = preferences.appearance === "system" ? "light dark" : preferences.appearance;
  document.documentElement.dataset.textSize = preferences.textSize;
}

export function updateLocale(preferences: MobilePreferences, locale: RequiredLocaleCode): MobilePreferences {
  return Object.freeze({ ...preferences, locale, localeSource: "manual" as const });
}

export function updateAppearance(preferences: MobilePreferences, appearance: AppearancePreference): MobilePreferences {
  return Object.freeze({ ...preferences, appearance });
}

export function updateTextSize(preferences: MobilePreferences, textSize: TextSizePreference): MobilePreferences {
  return Object.freeze({ ...preferences, textSize });
}
